import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import {
    IndividualTabPropsQuote,
    DisplayProps
} from "@/components/wpl/tab-group/src";
import { DEFAULT_VALUES } from "@/libraries/web-framework";
import TabsQuote from "@/patterns/wpl/tabs-quote/src";

import {
    getAutomateTabsSectionImage,
    getPlanTabsSectionImage,
    getPlanTabsSectionVideo,
    getReleaseTabsSectionImage,
    getReportTabsSectionImage,
    getTrackTabsSectionImage
} from "../../localized-images";

const TabsSection = () => {
    const intl = useIntl();

    const QUOTE_IMAGE_PROPS = {
        className: "tab-quote-image",
        src: "https://wac-cdn.atlassian.com/dam/jcr:5412ebd3-6135-4109-9ffc-666961b9bad2/quote.svg?cdnVersion=1220",
        alt: intl.formatMessage({
            id: "JiraSoftwarePageTemplate.tabs.quoteAlt",
            defaultMessage: "Quotation mark"
        })
    };

    const planLocalizedImageSrc = getPlanTabsSectionImage(intl.locale);
    const planLocalizedVideoSrc = getPlanTabsSectionVideo(intl.locale);
    const trackLocalizedImageSrc = getTrackTabsSectionImage(intl.locale);
    const releaseLocalizedImageSrc = getReleaseTabsSectionImage(intl.locale);
    const reportLocalizedImageSrc = getReportTabsSectionImage(intl.locale);
    const automateLocalizedImageSrc = getAutomateTabsSectionImage(intl.locale);

    const getPlanLocalizedMedia = (locale: string): DisplayProps => {
        // english should show a still image, other locales will show a localized gif (since the still has not been localized)
        if (locale === "en") {
            return {
                imageProps: {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:49c215de-3b05-40f8-96e2-6dc65c70caa6/Board.png?cdnVersion=1187",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsPlan.imageAltText",
                        defaultMessage: "" // TODO - still awaiting what this should be
                    }),
                    aspectRatio: "none"
                }
            };
        }
        return {
            videoProps: {
                videoLink: planLocalizedVideoSrc,
                videoPreviewImage: planLocalizedImageSrc,
                aspectRatio: "none"
            }
        };
    };

    const planLocalizedMedia = getPlanLocalizedMedia(intl.locale);

    const tabs: IndividualTabPropsQuote[] = [
        {
            textLabel: "Plan",
            id: "tab-plan",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.plan",
                defaultMessage: "Plan"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.plan",
                defaultMessage:
                    "Break the big ideas down into manageable chunks across teams with user stories, issues, and tasks."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.plan",
                defaultMessage:
                    "Work becomes a lot more visible when it's all in one place. It makes collaboration a whole lot easier."
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.plan",
                defaultMessage: "Jeff Lai"
            }),
            company: (
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.tabsCompany.plan"
                    defaultMessage="Internal Infrastructure, canva"
                    values={DEFAULT_VALUES}
                />
            ),
            display: planLocalizedMedia
        },
        {
            textLabel: "Track",
            id: "tab-track",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.track",
                defaultMessage: "Track"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.track",
                defaultMessage:
                    "Prioritize and discuss your team’s work in context with complete visibility at every level."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.track",
                defaultMessage:
                    "The pace of delivery from idea to reality has exploded."
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.track",
                defaultMessage: "Greg Franklin"
            }),
            company: (
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.tabsCompany.track"
                    defaultMessage="Agile lead, Cancer Research UK"
                    values={DEFAULT_VALUES}
                />
            ),
            display: {
                imageProps: {
                    src: trackLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.track",
                        defaultMessage:
                            "The timeline view in Jira allows users to review progress by sprints in weeks, months, or quarters. Status is by category in this example."
                    })
                }
            }
        },
        {
            textLabel: "Release",
            id: "tab-release",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.release",
                defaultMessage: "Release"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.release",
                defaultMessage:
                    "Ship faster, with confidence knowing the information you have is always up-to-date."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.release",
                defaultMessage:
                    "Less friction in the development process means better results and a faster time to market."
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.release",
                defaultMessage: "Louis Bennett"
            }),
            company: (
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.tabsCompany.release"
                    defaultMessage="Director of Engineering, Trulia"
                    values={DEFAULT_VALUES}
                />
            ),
            display: {
                imageProps: {
                    src: releaseLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.release",
                        defaultMessage:
                            "Releases within a Jira project. This view contains the version, status, progress, start date, release date, and description."
                    })
                }
            }
        },
        {
            textLabel: "Report",
            id: "tab-report",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.report",
                defaultMessage: "Report"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.report",
                defaultMessage:
                    "Improve team performance in context, based on real-time, visual data."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.report",
                defaultMessage:
                    "Full visibility in Jira across the whole organization enables you to make better management judgment. "
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.report",
                defaultMessage: "Paul Leaon"
            }),
            company: (
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.tabsCompany.report"
                    defaultMessage="Digital Portfolio Director, Inchcape"
                    values={DEFAULT_VALUES}
                />
            ),
            display: {
                imageProps: {
                    src: reportLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.report",
                        defaultMessage:
                            "Within the Kanban board in Jira, a project backlog shows 5 issues are on the board and 8 are in backlog."
                    })
                }
            }
        },
        {
            textLabel: "Automate",
            id: "tab-automate",
            headerText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsHeader.automate",
                defaultMessage: "Automate"
            }),
            bodyText: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsBody.automate",
                defaultMessage:
                    "Save time, keep your team focused, and help work flow with no-code automation."
            }),
            quoteImageProps: QUOTE_IMAGE_PROPS,
            quote: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsQuote.automate",
                defaultMessage:
                    "We’re saving 150 hours+ of valuable engineering time per month. "
            }),
            name: intl.formatMessage({
                id: "JiraSoftwarePageTemplate.tabsName.automate",
                defaultMessage: "Jeff Lai"
            }),
            company: (
                <FormattedMessage
                    id="JiraSoftwarePageTemplate.tabsCompany.automate"
                    defaultMessage="Internal Infrastructure, canva"
                    values={DEFAULT_VALUES}
                />
            ),
            display: {
                imageProps: {
                    src: automateLocalizedImageSrc,
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePageTemplate.tabsImageAltText.automate",
                        defaultMessage:
                            "Automation workflow in Jira. An example automation closes all stories in an epic when the epic is completed."
                    })
                }
            }
        }
    ];

    return (
        <section
            css={css({
                "letterSpacing": "0.3px",
                ".software-jira-tabs": {
                    backgroundImage:
                        "linear-gradient( 123.5deg, #091e42 78%, #0747a6 99.98% )",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover"
                },
                ".tab-group-inner": {
                    button: {
                        "border": "1px solid transparent",
                        "padding": "4px 16px",
                        "&:hover": {
                            border: "1px solid #c1c7d0"
                        },
                        "&:focus-visible": {
                            outline: "revert"
                        },
                        [media.below.md]: {
                            fontSize: "1.25rem"
                        }
                    },
                    [`[aria-selected="true"]`]: {
                        "&:hover": {
                            borderColor: "transparent"
                        }
                    }
                },
                ".select-container-inner": {
                    maxWidth: "388px",
                    width: "100%",
                    margin: "auto"
                },
                "#tabs-section": {
                    "border": "none",
                    "fontSize": "1rem",
                    "lineHeight": "40px",
                    "padding": "0 15px",
                    "width": "100%",
                    "&:focus-visible": {
                        outline: "revert"
                    }
                },
                ".tab-quote-image": {
                    width: "45px"
                },
                ".tab-panel": {
                    "padding": "16px 0",
                    ".row-layout": {
                        alignItems: "flex-start",
                        [media.below.sm]: {
                            img: {
                                order: -1
                            }
                        }
                    }
                },
                ".text-section": {
                    h2: {
                        textAlign: "center",
                        fontWeight: "500",
                        letterSpacing: "0.3px",
                        fontFamily: "var(--bxp-charlie-display-font-family)",
                        fontSize: "2.25rem",
                        margin: "0"
                    },
                    h3: {
                        fontFamily: "var(--bxp-charlie-display-font-family)",
                        fontSize: "2.25rem",
                        margin: 0,
                        marginBlockEnd: "24px"
                    },
                    p: {
                        fontFamily: "var(--bxp-charlie-text-font-family)",
                        marginBlockEnd: "24px",
                        fontSize: "1.25rem",
                        fontWeight: "normal",
                        letterSpacing: "0.3px",
                        lineHeight: 1.4
                    },
                    [media.below.sm]: {
                        "h2, h3": {
                            fontSize: "1.5rem"
                        }
                    }
                },
                ".quote-wrapper": {
                    borderRadius: "0px 150px 150px 0px",
                    [media.below.md]: {
                        display: "none"
                    }
                },
                ".quote-blurb": {
                    fontSize: "1.25rem",
                    lineHeight: 1.2
                },
                ".quote-name": {
                    fontSize: "0.875rem",
                    lineHeight: 1.25,
                    width: "75%"
                },
                ".quote-company": {
                    fontSize: "0.75rem !important",
                    lineHeight: 2,
                    width: "75%"
                },
                "p": {
                    fontSize: "1.25rem",
                    lineHeight: 1.2
                }
            })}
        >
            <TabsQuote
                id="tabs-section"
                label="Tabs"
                headerText={intl.formatMessage({
                    id: "JiraSoftwarePageTemplate.tabsHeader",
                    defaultMessage: "All from a single source of truth"
                })}
                bodyText=""
                tabs={tabs}
                colors={{
                    defaultBG: "#42526e",
                    defaultText: "#dfe1e6",
                    activeBG: "#57d9a3",
                    activeText: "#091e42",
                    hoverBG: "#42526e",
                    hoverText: "#dfe1e6",
                    quoteColor: "#0065ff"
                }}
                className="software-jira-tabs"
            />
        </section>
    );
};

TabsSection.displayName = "TabsSection";

export default TabsSection;
